import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Poll every 30s, skip while the tab is hidden, re-check as soon as it is visible again.
const POLL_MS = 30000;

type StatusPayload = {
  notice?: boolean;
  message?: string;
};

export function MaintenanceBanner() {
  const { t } = useTranslation();
  const fallback = t("deploy_notice");
  const [notice, setNotice] = useState(false);
  const [message, setMessage] = useState(fallback);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const base = import.meta.env.VITE_API_URL || "";
        const response = await fetch(`${base}/api/v1/status`);
        if (!alive) return;
        if (!response.ok) {
          if (import.meta.env.PROD && response.status >= 500) {
            setNotice(true);
          }
          return;
        }
        const data = (await response.json()) as StatusPayload;
        setNotice(Boolean(data.notice));
        if (typeof data.message === "string" && data.message.trim()) {
          setMessage(data.message);
        }
      } catch {
        if (import.meta.env.PROD) {
          setNotice(true);
        }
      }
    };
    void tick();
    const id = window.setInterval(() => {
      if (!document.hidden) void tick();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!notice) return null;

  return (
    <div
      role="status"
      data-testid="maintenance-banner"
      className="bg-amber-100 text-amber-950 text-sm px-4 py-2 text-center border-b border-amber-300 z-50"
    >
      {message}
    </div>
  );
}
